// CatalogBookDetails.tsx
import React, { useState, useEffect, ChangeEvent } from "react";
import axios from "axios";
import { useAuth } from '../../global-components/src';
import { useMessage } from "../../global-components/src/index";
import { useNavigate, useParams } from "react-router-dom";
import { BUCKET_READBOOKS_URL, READBOOKS_API_URL, READBOOKS_URL } from "../../global-components/src/utils/vars";
import { clearText, errorMessage } from "../../global-components/src/utils/text";
import Modal from "../../global-components/src/components/modal/modal";
import { BookOpenText } from "lucide-react";
import NotFound from "../errors/NotFound";
import { isMyLove } from "../../global-components/src/utils/auth";
import { BookPlaceholder } from "../books/placeholder";
import Spinner from "../../global-components/src/components/spinner/spiner";
import { CatalogBook } from "../../types";

// ─── Tipos ────────────────────────────────────────────────────────────────────


interface Form {
    paginas_lidas: number;
    data_inicio: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface ShowResponse {
    book: CatalogBook;
    can_read: boolean;
    has_purchased: boolean;
}

const formatCurrency = (value: number, currency = "BRL") =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency }).format(value);

const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleDateString("pt-BR") : null;

const formatBytes = (bytes: number | null) => {
    if (!bytes) return null;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ─── Componente ───────────────────────────────────────────────────────────────

export default function CatalogBookDetails() {
    const [showAdd, setShowAdd]           = useState(false);
    const [showShare, setShowShare]       = useState(false);
    const [loading, setLoading]           = useState(false);
    const [form, setForm]                 = useState<Form | null>(null);
    const [loadingAdd, setLoadingAdd]     = useState(false);
    const [book, setBook]                 = useState<CatalogBook | null>(null);
    const [canRead, setCanRead]           = useState(false);
    const [hasPurchased, setHasPurchased] = useState(false);
    const [errorStatusCode, setErrorStatusCode] = useState<404 | null>(null);
    const [errorsInput, setErrorsInput]   = useState<any>({});

    const { token, user } = useAuth();
    const { setMessage }  = useMessage();
    const { id }          = useParams();
    const navigate        = useNavigate();

    const shareUrl = `${READBOOKS_URL}/catalogo/${book?.uuid}`;

    // ── Carrega dados do livro ────────────────────────────────────────────────

    const loadData = async () => {
        if (loading) return;
        setLoading(true);
        try {
            const response = await axios.get<ShowResponse>(
                `${READBOOKS_API_URL}/catalog/${id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setBook(response.data.book);
            setCanRead(response.data.can_read);
            setHasPurchased(response.data.has_purchased);
        } catch (err: any) {
            const status = err.response?.status;
            if (status === 404) {
                setErrorStatusCode(404);
            } else {
                setMessage(errorMessage(err), "error");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) loadData();
    }, [id]);

    // ── Copiar link ───────────────────────────────────────────────────────────

    const copyLink = () => {
        navigator.clipboard.writeText(shareUrl);
    };

    // ── Formulário de adicionar à leitura ─────────────────────────────────────

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const target  = e.target as HTMLInputElement;
        const { name, value, type } = target;
        const checked = target.checked;

        setForm((prev) => {
            setErrorsInput((prevErrors: any) => ({ ...prevErrors, [name]: null }));

            return {
                ...(prev ?? { paginas_lidas: 0, data_inicio: "" }),
                [name]: type === "checkbox" ? checked : value,
            };
        });
    };

    const handleSubmit = async () => {
        if (loadingAdd) return;
        setLoadingAdd(true);

        try {
            const errors: Record<string, string[]> = {};

            if (form?.data_inicio && new Date(form.data_inicio).getTime() > Date.now()) {
                errors.data_inicio = ["A data de início não pode ser no futuro."];
            }

            if (Object.keys(errors).length > 0) {
                throw { response: { data: { errors } } };
            }

            const response = await axios.post(
                `${READBOOKS_API_URL}/catalog/${id}/add-to-read`,
                {
                    pages_read: form?.paginas_lidas ?? 0,
                    start_date: form?.data_inicio || null,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (user?.id) {
                localStorage.removeItem(`ai_reading_analysis_${user.id}`);
            }
            localStorage.removeItem("ai_reading_analysis");
            setShowAdd(false);
            setMessage(response.data.message, "success");
            navigate(`/livros/${response.data.book.uuid}`);
        } catch (err: any) {
            if (err?.response?.data?.errors) {
                setErrorsInput(err.response.data.errors);
                return;
            }
            setMessage(errorMessage(err), "error");
        } finally {
            setLoadingAdd(false);
        }
    };

    // ── Botão de leitura — lógica condicional ─────────────────────────────────

    const ReadButton = () => {
        // 1. Pode ler (grátis ou já comprou)
        if (canRead) {
            return (
                <>
                    <button
                        disabled
                        className="btn-livro btn btn-success"
                        style={{ width: "100%", marginTop: "10px" }}
                    >
                        Ler agora
                    </button>
                    <p style={{ marginBottom: 7, marginTop: 2 }}>Adicione a leitura para acessar o PDF</p>
                </>
            );
        }

        // 2. Livro pago — ainda não comprou
        if (book && book.price > 0 && !hasPurchased) {
            return (
                <button
                    onClick={() => navigate(`/catalogo/${id}/comprar`)}
                    className="btn-livro btn btn-warning"
                    style={{ width: "100%", marginTop: "10px" }}
                >
                    Comprar por {formatCurrency(book.price, book.currency)}
                </button>
            );
        }

        // 3. PDF ainda não disponível (edge case: grátis mas sem arquivo)
        return null;
    };

    // ── Capa ──────────────────────────────────────────────────────────────────

    const coverSrc = `${BUCKET_READBOOKS_URL}/${book?.cover_url}` || "/img/book_transparent.png";
    const hasDefaultCover = !book?.cover_url;

    // ── Early returns ─────────────────────────────────────────────────────────

    if (errorStatusCode === 404) {
        return (
            <NotFound text="Livro não encontrado" showLink={false}>
                <BookOpenText className="icon-empty" />
            </NotFound>
        );
    }

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="margin-top-menu container container-livro">

            {/* Modal — compartilhar */}
            <Modal
                textBtnConfirmar="Ok"
                idModal="modal-share"
                coverMobile={false}
                onOpen={copyLink}
                onClose={() => setShowShare(false)}
                title={`Link copiado${isMyLove(user) ? ", meu amor" : ""}!`}
                text={`O link do livro foi copiado${isMyLove(user) ? ", meu lovezinho" : ""}: ${shareUrl}`}
                show={showShare}
                handleConfirm={() => setShowShare(false)}
                isLoading={false}
            />

            {/* Modal — adicionar à leitura */}
            <Modal
                textBtnConfirmar="Adicionar"
                idModal="modal-add"
                onClose={() => setShowAdd(false)}
                title={`Adicionar livro${isMyLove(user) ? ", meu amor" : ""}`}
                text={`Deixe páginas lidas em branco para adicionar à lista de desejos${isMyLove(user) ? ", meu lovezinho" : ""}`}
                show={showAdd}
                handleConfirm={handleSubmit}
                isLoading={loadingAdd}
                textBtnCancelar="Cancelar"
                extraContent={
                    <>
                        <div className="mb-3">
                            <label htmlFor="paginas_lidas" className="label">Páginas lidas</label>
                            <input
                                onChange={handleChange}
                                type="number"
                                className={`form-control ${errorsInput.paginas_lidas ? "is-invalid" : ""}`}
                                id="paginas_lidas"
                                name="paginas_lidas"
                                value={form?.paginas_lidas ?? ""}
                            />
                            {errorsInput.paginas_lidas && (
                                <div className="invalid-feedback" style={{ display: "block" }}>
                                    {errorsInput.paginas_lidas[0]}
                                </div>
                            )}
                        </div>
                        {form?.paginas_lidas && form.paginas_lidas > 0 && (
                            <div className="mb-4">
                                <label htmlFor="data_inicio" className="label">Data de início</label>
                                <input
                                    onChange={handleChange}
                                    type="date"
                                    className={`form-control ${errorsInput.data_inicio ? "is-invalid" : ""}`}
                                    id="data_inicio"
                                    name="data_inicio"
                                    value={form?.data_inicio ?? ""}
                                />
                                {errorsInput.data_inicio && (
                                    <div className="invalid-feedback" style={{ display: "block" }}>
                                        {errorsInput.data_inicio[0]}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                }
            />

            <div className="row">
                {/* ── Capa ── */}
                <div className="col-4">
                    {loading && !book ? (
                        <BookPlaceholder className="img-livro-selecionado" />
                    ) : (
                        <div
                            className={`div-img-livro-selecionado ${hasDefaultCover ? "img-capa-default" : ""}`}
                        >
                            {!hasDefaultCover ? (
                                <img
                                    onError={(e) => { e.currentTarget.src = "/img/book_transparent.png"; }}
                                    src={coverSrc}
                                    alt="Capa do livro"
                                    className="img-livro-selecionado"
                                />
                            ) : (
                                <h1>{book?.title}</h1>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Informações ── */}
                {(!loading || book) && (
                    <div className="col-8 col-info-livro">
                        <h1 className="nome-livro-lido">{book?.title}</h1>

                        <div className="row row-livro-lido">
                            <div className="col">
                                <p>
                                    <b>Autor:</b> {book?.author || "Desconhecido"} <br />
                                    {book?.price != null && (
                                        <>
                                            <b>Preço:</b>{" "}
                                            {book.price === 0
                                                ? "Gratuito"
                                                : formatCurrency(book.price, book.currency)
                                            }{" "}
                                            <br />
                                        </>
                                    )}
                                    {book?.file_size_bytes && (
                                        <>
                                            <b>Tamanho:</b> {formatBytes(book.file_size_bytes)} <br />
                                        </>
                                    )}
                                </p>
                                <p>
                                    <b>Adicionado em:</b> {formatDate(book?.created_at) || "Desconhecido"} <br />
                                    <b>Atualizado em:</b> {formatDate(book?.updated_at) || "Desconhecido"} <br />
                                    <b>Tipo:</b>{" "}
                                    {book?.type === "own_work" ? "Obra própria" : "Google Books"} <br />
                                </p>
                            </div>

                            {/* ── Botões ── */}
                            <div className="col col-btn-google-book">
                                <button
                                    onClick={() => setShowAdd(true)}
                                    className="btn-livro btn btn-primary"
                                    style={{ width: "100%", marginTop: "10px" }}
                                >
                                    Adicionar à leitura
                                </button>

                                <ReadButton />

                                <button
                                    onClick={() => setShowShare(true)}
                                    className="btn-livro btn btn-outline-primary"
                                    style={{ width: "100%", marginTop: "10px" }}
                                >
                                    Compartilhar
                                </button>
                            </div>

                            {/* ── Descrição ── */}
                            {book?.description && (
                                <p
                                    dangerouslySetInnerHTML={{
                                        __html: clearText(book.description, false),
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}